import { gql } from "@apollo/client";
import type { PermitStatus, PermitApplicationStatus, PermitSummary, ISODateTime } from "common/types/permits";
import { OrderBy } from "../queryString";

export const GET_PERMITS = gql`
  query GetPermits($offset: Int = 0, $limit: Int = 100, $orderBy: [permit_order_by!], $filter: permit_bool_exp) {
    permits: permit(offset: $offset, limit: $limit, order_by: $orderBy, where: $filter) {
      id
      displayId: display_id
      status
      applicationStatus: application_status
      applicationSubmittedAt: application_submitted_at
      validFrom: valid_from
      validTo: valid_to
      createdAt: created_at
      payerAccount: payer_account {
        id
        holderName: holder_name
        organizationName: organization_name
      }
      permitType: permit_type {
        id
        name
      }
      vehicleCount: permit_vehicles_aggregate {
        aggregate {
          count
        }
      }
    }
    permitsAggregate: permit_aggregate(where: $filter) {
      aggregate {
        count
      }
    }
  }
`;

/** Raw permit data from GraphQL response */
export interface GetPermitsPermitRaw {
  id: string;
  displayId: string;
  status: PermitStatus;
  applicationStatus: PermitApplicationStatus;
  applicationSubmittedAt: ISODateTime | null;
  validFrom: ISODateTime | null;
  validTo: ISODateTime | null;
  createdAt: ISODateTime;
  payerAccount: {
    id: string;
    holderName: string;
    organizationName: string | null;
  } | null;
  permitType: {
    id: string;
    name: string;
  };
  vehicleCount: {
    aggregate: {
      count: number;
    };
  };
}

export interface GetPermitsVars {
  offset: number;
  limit: number;
  orderBy?: PermitsOrderBy[];
  filter?: GetPermitsWhereFilter;
}

export interface GetPermitsData {
  permits: GetPermitsPermitRaw[];
  permitsAggregate: {
    aggregate: {
      count: number;
    };
  };
}

// Filter types for GraphQL where clause
export interface GetPermitsWhereFilter {
  display_id?: { _eq?: string; _ilike?: string };
  status?: { _neq?: PermitStatus; _eq?: PermitStatus; _in?: PermitStatus[] };
  application_status?: { _eq?: PermitApplicationStatus; _in?: PermitApplicationStatus[] };
  application_submitted_by_user_id?: { _is_null?: boolean };
  application_submitted_at?: { _eq?: string; _lt?: string; _gt?: string; _gte?: string; _lte?: string };
  valid_from?: { _eq?: string; _lt?: string; _gt?: string; _gte?: string; _lte?: string };
  valid_to?: { _eq?: string; _lt?: string; _gt?: string; _gte?: string; _lte?: string };
  payer_account?: {
    holder_name?: { _eq?: string; _ilike?: string };
  };
  permit_type?: {
    name?: { _eq?: string };
    id?: { _eq?: string };
  };
  _and?: GetPermitsWhereFilter[];
  _or?: GetPermitsWhereFilter[];
}

// Order by types
export type PermitsOrderBy =
  | { display_id: OrderBy }
  | { status: OrderBy }
  | { application_status: OrderBy }
  | { application_submitted_at: OrderBy }
  | { valid_from: OrderBy }
  | { valid_to: OrderBy }
  | { created_at: OrderBy }
  | { payer_account: { holder_name: OrderBy } }
  | { permit_type: { name: OrderBy } };

/** Transform raw GraphQL response to PermitSummary */
export function transformPermitRaw(raw: GetPermitsPermitRaw): PermitSummary {
  return {
    id: raw.id,
    displayId: raw.displayId,
    status: raw.status,
    applicationStatus: raw.applicationStatus,
    applicationSubmittedAt: raw.applicationSubmittedAt,
    validFrom: raw.validFrom,
    validTo: raw.validTo,
    createdAt: raw.createdAt,
    payerAccount: raw.payerAccount
      ? {
          id: raw.payerAccount.id,
          holderName: raw.payerAccount.holderName,
          organizationName: raw.payerAccount.organizationName,
        }
      : null,
    permitType: {
      id: raw.permitType.id,
      name: raw.permitType.name,
    },
    vehicleCount: raw.vehicleCount.aggregate.count,
  };
}
